// Sound manager for casino UI
// Handles click, chip placement, and ball spin sounds with fade effects

class SoundManager {
  constructor() {
    this.sounds = {};
    this.spinAudio = null;
    this.fadeInterval = null;
    this.initialized = false;

    // Volume change listeners for cross-game sync
    this._volumeListeners = [];

    // Master volume (0-1), stored in localStorage
    this.masterVolume = this.loadVolume();

    // Base volumes for each sound type
    this.baseVolumes = {
      click: 0.8,
      chipPlaced: 0.7,
      spin: 0.3,
      cardDraw: 0.6,
      cardFlip: 0.35, // Lower than cardDraw as audio file is intrinsically louder
      diamondHit: 0.7,
      mineHit: 0.8,
      kenoSelect: 0.25,
      kenoHit: 0.3,
      kenoMiss: 0.2,
      limboLoop: 0.4,
      limboWin: 0.7,
      diceLoop: 0.4,
      diceWin: 0.7,
      // Chicken Cross sounds
      chickenHop: 0.6,
      chickenSplash: 0.7,
      chickenVictory: 0.7,
      chickenCashout: 0.7,
      chickenHonk: 0.4,
      chickenCarPassing: 0.15
    };

    // Looping audio instances
    this.limboLoopAudio = null;
    this.diceLoopAudio = null;

    // Rapid-fire interval refs
    this.diceRapidInterval = null;
    this.limboRapidInterval = null;

    // Track which sound groups have been loaded
    this._loadedGroups = new Set();
  }

  // Load volume from localStorage, default to 0.7
  loadVolume() {
    try {
      const saved = localStorage.getItem('casino_sound_volume');
      if (saved !== null) {
        const vol = parseFloat(saved);
        return isNaN(vol) ? 0.7 : Math.max(0, Math.min(1, vol));
      }
    } catch (e) {}
    return 0.7;
  }

  // Save volume to localStorage
  saveVolume(volume) {
    try {
      localStorage.setItem('casino_sound_volume', volume.toString());
    } catch (e) {}
  }

  // Get current master volume
  getVolume() {
    return this.masterVolume;
  }

  // Set master volume (0-1)
  setVolume(volume) {
    this.masterVolume = Math.max(0, Math.min(1, volume));
    this.saveVolume(this.masterVolume);

    // Update currently playing spin sound if any
    if (this.spinAudio && !this.spinAudio.paused) {
      this.spinAudio.volume = this.baseVolumes.spin * this.masterVolume;
    }

    this._notifyVolumeChange();
  }

  // Check if sound is muted
  isMuted() {
    return this.masterVolume === 0;
  }

  // Toggle mute
  toggleMute() {
    if (this.masterVolume > 0) {
      this._previousVolume = this.masterVolume;
      this.setVolume(0);
    } else {
      this.setVolume(this._previousVolume || 0.7);
    }
    return this.masterVolume;
  }

  // Subscribe to volume changes — returns unsubscribe function
  onVolumeChange(cb) {
    this._volumeListeners.push(cb);
    return () => { this._volumeListeners = this._volumeListeners.filter(l => l !== cb); };
  }

  _notifyVolumeChange() {
    const vol = this.masterVolume;
    for (const cb of this._volumeListeners) cb(vol);
  }

  // Core sounds only (~120KB) — click, chip, win/lose
  init() {
    if (this.initialized) return;

    this.sounds.click = new Audio('/sounds/click.wav');
    this.sounds.chipPlaced = new Audio('/sounds/chips_placed.wav');
    this.sounds.diamondHit = new Audio('/sounds/diamond_hit.wav');
    this.sounds.mineHit = new Audio('/sounds/boom.wav');

    this.initialized = true;
  }

  // Load a game-specific sound group on first use
  _ensureGroup(name) {
    if (!this.initialized) this.init();
    if (this._loadedGroups.has(name)) return;
    this._loadedGroups.add(name);

    switch (name) {
      case 'roulette':
        this.spinAudio = new Audio('/sounds/ball_spin.wav');
        this.spinAudio.loop = false;
        this.spinAudio.volume = 0;
        break;

      case 'blackjack':
        this.sounds.cardDraw = new Audio('/sounds/card-draw.mp3');
        this.sounds.cardFlip = new Audio('/sounds/card-flip.mp3');
        break;

      case 'keno':
        this.sounds.kenoSelect = new Audio('/sounds/keno2.sound');
        this.sounds.kenoHit = new Audio('/sounds/keno2.sound');
        this.sounds.kenoMiss = new Audio('/sounds/keno1.sound');
        break;

      case 'limbo':
        this.sounds.limboWin = new Audio('/sounds/limbo2.sound');
        this.limboLoopAudio = new Audio('/sounds/limbo1.sound');
        break;

      case 'dice':
        this.sounds.diceWin = new Audio('/sounds/dice2.sound');
        this.diceLoopAudio = new Audio('/sounds/dice1.sound');
        break;

      case 'chicken':
        this.sounds.chickenHop = new Audio('/sounds/hopsound.wav');
        this.sounds.chickenSplash = new Audio('/sounds/splash.wav');
        this.sounds.chickenVictory = new Audio('/sounds/victory.wav');
        this.sounds.chickenCashout = new Audio('/sounds/cashout.wav');
        this.sounds.chickenHonk = new Audio('/sounds/honk.wav');
        this.sounds.chickenCarPassing = new Audio('/sounds/carpassing.wav');
        break;
    }
  }

  // Calculate actual volume based on master volume
  getActualVolume(baseVolume) {
    return baseVolume * this.masterVolume;
  }

  _createPool(sound, size = 4) {
    return { nodes: Array.from({ length: size }, () => sound.cloneNode()), index: 0 };
  }

  _playFromPool(pool, volume) {
    const node = pool.nodes[pool.index];
    pool.index = (pool.index + 1) % pool.nodes.length;
    node.currentTime = 0;
    node.volume = volume;
    node.play().catch(() => {});
  }

  _ensurePool(name) {
    if (this._pools) return;
    this._pools = {};
  }

  _getPool(soundName, size = 4) {
    this._ensurePool();
    if (!this._pools[soundName] && this.sounds[soundName]) {
      this._pools[soundName] = this._createPool(this.sounds[soundName], size);
    }
    return this._pools[soundName];
  }

  playClick() {
    if (!this.initialized) this.init();
    if (this.masterVolume === 0) return;
    const pool = this._getPool('click', 4);
    if (pool) this._playFromPool(pool, this.getActualVolume(this.baseVolumes.click));
  }

  playChipPlaced() {
    if (!this.initialized) this.init();
    if (this.masterVolume === 0) return;
    const pool = this._getPool('chipPlaced', 4);
    if (pool) this._playFromPool(pool, this.getActualVolume(this.baseVolumes.chipPlaced));
  }

  playCardDraw() {
    this._ensureGroup('blackjack');
    if (this.masterVolume === 0) return;
    const pool = this._getPool('cardDraw', 4);
    if (pool) this._playFromPool(pool, this.getActualVolume(this.baseVolumes.cardDraw));
  }

  playCardFlip() {
    this._ensureGroup('blackjack');
    if (this.masterVolume === 0) return;
    const pool = this._getPool('cardFlip', 3);
    if (pool) this._playFromPool(pool, this.getActualVolume(this.baseVolumes.cardFlip));
  }

  playDiamondHit() {
    if (!this.initialized) this.init();
    if (this.masterVolume === 0) return;
    const pool = this._getPool('diamondHit', 4);
    if (pool) this._playFromPool(pool, this.getActualVolume(this.baseVolumes.diamondHit));
  }

  playMineHit() {
    if (!this.initialized) this.init();
    if (this.masterVolume === 0) return;
    const pool = this._getPool('mineHit', 4);
    if (pool) this._playFromPool(pool, this.getActualVolume(this.baseVolumes.mineHit));
  }

  playDiceRoll() {
    if (!this.initialized) this.init();
    if (this.masterVolume === 0) return;
    const pool = this._getPool('click', 4);
    if (pool) this._playFromPool(pool, this.getActualVolume(this.baseVolumes.click));
  }

  playWin() {
    if (!this.initialized) this.init();
    if (this.masterVolume === 0) return;
    const pool = this._getPool('diamondHit', 4);
    if (pool) this._playFromPool(pool, this.getActualVolume(this.baseVolumes.diamondHit));
  }

  playLose() {
    if (!this.initialized) this.init();
    if (this.masterVolume === 0) return;
    const pool = this._getPool('mineHit', 4);
    if (pool) this._playFromPool(pool, this.getActualVolume(this.baseVolumes.mineHit * 0.6));
  }

  playKenoSelect() {
    this._ensureGroup('keno');
    if (this.masterVolume === 0) return;
    const pool = this._getPool('kenoSelect', 4);
    if (pool) this._playFromPool(pool, this.getActualVolume(this.baseVolumes.kenoSelect));
  }

  playKenoHit() {
    this._ensureGroup('keno');
    if (this.masterVolume === 0) return;
    const pool = this._getPool('kenoHit', 6);
    if (pool) this._playFromPool(pool, this.getActualVolume(this.baseVolumes.kenoHit));
  }

  playKenoMiss() {
    this._ensureGroup('keno');
    if (this.masterVolume === 0) return;
    const pool = this._getPool('kenoMiss', 4);
    if (pool) this._playFromPool(pool, this.getActualVolume(this.baseVolumes.kenoMiss));
  }

  startLimboLoop() {
    this._ensureGroup('limbo');
    if (this.masterVolume === 0) return;
    this.stopLimboLoop();

    const targetVolume = this.getActualVolume(this.baseVolumes.limboLoop);
    if (!this._pools) this._pools = {};
    if (!this._pools.limboLoop) {
      this._pools.limboLoop = this._createPool(this.limboLoopAudio, 3);
    }
    const pool = this._pools.limboLoop;

    this._playFromPool(pool, targetVolume);
    this.limboRapidInterval = setInterval(() => this._playFromPool(pool, targetVolume), 60);
  }

  stopLimboLoop() {
    if (this.limboRapidInterval) {
      clearInterval(this.limboRapidInterval);
      this.limboRapidInterval = null;
    }
  }

  playLimboWin() {
    this._ensureGroup('limbo');
    if (this.masterVolume === 0) return;
    const pool = this._getPool('limboWin', 3);
    if (pool) this._playFromPool(pool, this.getActualVolume(this.baseVolumes.limboWin));
  }

  startDiceLoop() {
    this._ensureGroup('dice');
    if (this.masterVolume === 0) return;
    this.stopDiceLoop();

    const targetVolume = this.getActualVolume(this.baseVolumes.diceLoop);
    if (!this._pools) this._pools = {};
    if (!this._pools.diceLoop) {
      this._pools.diceLoop = this._createPool(this.diceLoopAudio, 3);
    }
    const pool = this._pools.diceLoop;

    this._playFromPool(pool, targetVolume);
    this.diceRapidInterval = setInterval(() => this._playFromPool(pool, targetVolume), 80);
  }

  stopDiceLoop() {
    if (this.diceRapidInterval) {
      clearInterval(this.diceRapidInterval);
      this.diceRapidInterval = null;
    }
  }

  playDiceWin() {
    this._ensureGroup('dice');
    if (this.masterVolume === 0) return;
    const pool = this._getPool('diceWin', 3);
    if (pool) this._playFromPool(pool, this.getActualVolume(this.baseVolumes.diceWin));
  }

  playHop() {
    this._ensureGroup('chicken');
    if (this.masterVolume === 0) return;
    const pool = this._getPool('chickenHop', 4);
    if (pool) this._playFromPool(pool, this.getActualVolume(this.baseVolumes.chickenHop));
  }

  playChickenSplash() {
    this._ensureGroup('chicken');
    if (this.masterVolume === 0) return;
    const pool = this._getPool('chickenSplash', 3);
    if (pool) this._playFromPool(pool, this.getActualVolume(this.baseVolumes.chickenSplash));
  }

  playChickenVictory() {
    this._ensureGroup('chicken');
    if (this.masterVolume === 0) return;
    const pool = this._getPool('chickenVictory', 3);
    if (pool) this._playFromPool(pool, this.getActualVolume(this.baseVolumes.chickenVictory));
  }

  playChickenCashout() {
    this._ensureGroup('chicken');
    if (this.masterVolume === 0) return;
    const pool = this._getPool('chickenCashout', 3);
    if (pool) this._playFromPool(pool, this.getActualVolume(this.baseVolumes.chickenCashout));
  }

  playHonk() {
    this._ensureGroup('chicken');
    if (this.masterVolume === 0) return;
    const pool = this._getPool('chickenHonk', 3);
    if (pool) this._playFromPool(pool, this.getActualVolume(this.baseVolumes.chickenHonk));
  }

  playCarPassing() {
    this._ensureGroup('chicken');
    if (this.masterVolume === 0) return;
    const pool = this._getPool('chickenCarPassing', 3);
    if (pool) this._playFromPool(pool, this.getActualVolume(this.baseVolumes.chickenCarPassing));
  }

  startSpinSound() {
    this._ensureGroup('roulette');
    if (this.masterVolume === 0) return;

    // Clear any existing fade
    if (this.fadeInterval) {
      clearInterval(this.fadeInterval);
      this.fadeInterval = null;
    }

    const targetVolume = this.getActualVolume(this.baseVolumes.spin);

    // Reset and start playing (no loop - sound should be 6-7 seconds)
    this.spinAudio.currentTime = 0;
    this.spinAudio.volume = 0;
    this.spinAudio.play().catch(() => {});

    // Quick fade in (200ms)
    const fadeInDuration = 200;
    const steps = 10;
    const increment = targetVolume / steps;
    let step = 0;

    this.fadeInterval = setInterval(() => {
      step++;
      this.spinAudio.volume = Math.min(targetVolume, increment * step);

      if (step >= steps) {
        clearInterval(this.fadeInterval);
        this.fadeInterval = null;
        this.spinAudio.volume = targetVolume;
      }
    }, fadeInDuration / steps);
  }

  // Call this when deceleration starts - fades out over the given duration
  fadeOutSpinSound(duration = 3500) {
    if (!this.spinAudio || this.spinAudio.paused) return;

    // Clear any existing fade
    if (this.fadeInterval) {
      clearInterval(this.fadeInterval);
      this.fadeInterval = null;
    }

    const startVolume = this.spinAudio.volume;
    if (startVolume <= 0) return;

    // Fade out with easing - slower at start, faster at end
    const steps = 60;
    let step = 0;

    this.fadeInterval = setInterval(() => {
      step++;
      const progress = step / steps;

      // Ease-in curve: starts slow, ends fast
      const easedProgress = progress * progress;

      this.spinAudio.volume = Math.max(0, startVolume * (1 - easedProgress));

      if (step >= steps) {
        clearInterval(this.fadeInterval);
        this.fadeInterval = null;
        this.spinAudio.pause();
        this.spinAudio.currentTime = 0;
        this.spinAudio.volume = 0;
      }
    }, duration / steps);
  }

  stopSpinSound() {
    if (this.fadeInterval) {
      clearInterval(this.fadeInterval);
      this.fadeInterval = null;
    }

    if (this.spinAudio) {
      this.spinAudio.pause();
      this.spinAudio.currentTime = 0;
      this.spinAudio.volume = 0;
    }
  }
}

// Singleton instance
const soundManager = new SoundManager();

export default soundManager;
